import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Bell,
  CalendarClock,
  FileText,
  MapPin,
  Plus,
  Trash2,
  Save,
  Youtube,
  Image as ImageIcon,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { settingsApi, readError } from "@/lib/api";

const uid = (prefix) => `${prefix}_${Math.random().toString(36).slice(2, 10)}`;

const Card = ({ icon: Icon, eyebrow, title, description, children, enabled, onToggle, testid }) => (
  <div
    className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm md:p-8"
    data-testid={testid}
  >
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-apicon-mist text-apicon-navy">
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
            {eyebrow}
          </div>
          <div className="mt-1 font-display text-xl font-semibold text-apicon-navy md:text-2xl">
            {title}
          </div>
          <p className="mt-1 max-w-xl text-sm text-gray-600">{description}</p>
        </div>
      </div>
      <label className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-apicon-navy">
        <Switch checked={!!enabled} onCheckedChange={onToggle} data-testid={`${testid}-toggle`} />
        {enabled ? "Visible on Home" : "Hidden"}
      </label>
    </div>
    <div className="mt-6 space-y-4">{children}</div>
  </div>
);

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold uppercase tracking-[0.16em] text-apicon-navy/80">
      {label}
    </Label>
    {children}
    {hint && <p className="text-[11px] text-gray-500">{hint}</p>}
  </div>
);

const AdminExtras = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await settingsApi.get();
        setS(data || {});
      } catch (err) {
        toast.error(readError(err) || "Could not load settings.");
      }
    })();
  }, []);

  const patch = (upd) => setS((prev) => ({ ...(prev || {}), ...upd }));

  const save = async () => {
    if (!s) return;
    setSaving(true);
    try {
      const saved = await settingsApi.update(s);
      setS(saved);
      toast.success("Saved. Home page updated.");
    } catch (err) {
      toast.error(readError(err) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (!s) {
    return (
      <div className="rounded-3xl border border-gray-100 bg-white p-8 text-center text-gray-500">
        Loading extras…
      </div>
    );
  }

  // ── News ticker ────────────────────────────────────────────────────
  const newsItems = s.newsItems || [];
  const addNews = () =>
    patch({ newsItems: [...newsItems, { id: uid("n"), text: "", url: "", active: true }] });
  const updNews = (idx, u) =>
    patch({ newsItems: newsItems.map((n, i) => (i === idx ? { ...n, ...u } : n)) });
  const delNews = (idx) => patch({ newsItems: newsItems.filter((_, i) => i !== idx) });

  // ── Abstract chips ─────────────────────────────────────────────────
  const chips = s.abstractChips || [];
  const addChip = () => patch({ abstractChips: [...chips, { id: uid("c"), text: "" }] });
  const updChip = (idx, text) =>
    patch({ abstractChips: chips.map((c, i) => (i === idx ? { ...c, text } : c)) });
  const delChip = (idx) => patch({ abstractChips: chips.filter((_, i) => i !== idx) });

  // ── Tourism ────────────────────────────────────────────────────────
  const tvids = s.tourismVideos || [];
  const addTvid = () =>
    patch({ tourismVideos: [...tvids, { id: uid("tv"), youtubeId: "", caption: "" }] });
  const updTvid = (idx, u) =>
    patch({ tourismVideos: tvids.map((v, i) => (i === idx ? { ...v, ...u } : v)) });
  const delTvid = (idx) => patch({ tourismVideos: tvids.filter((_, i) => i !== idx) });

  const tphotos = s.tourismPhotos || [];
  const addTphoto = () =>
    patch({ tourismPhotos: [...tphotos, { id: uid("tp"), url: "", caption: "" }] });
  const updTphoto = (idx, u) =>
    patch({ tourismPhotos: tphotos.map((p, i) => (i === idx ? { ...p, ...u } : p)) });
  const delTphoto = (idx) => patch({ tourismPhotos: tphotos.filter((_, i) => i !== idx) });

  return (
    <div className="space-y-6" data-testid="admin-extras-root">
      {/* 1 · Countdown ─────────────────────────────────────────────── */}
      <Card
        testid="admin-extras-countdown"
        icon={CalendarClock}
        eyebrow="Countdown"
        title="Live event countdown"
        description="Shows days/hours/minutes/seconds until the conference kicks off — with an urgency headline."
        enabled={s.countdownEnabled}
        onToggle={(v) => patch({ countdownEnabled: v })}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Field
            label="Event start (local time)"
            hint="ISO 8601 format. Example: 2026-12-18T09:00:00+05:30"
          >
            <Input
              value={s.countdownEventIso || ""}
              onChange={(e) => patch({ countdownEventIso: e.target.value })}
              placeholder="2026-12-18T09:00:00+05:30"
              data-testid="admin-extras-countdown-iso"
            />
          </Field>
          <Field
            label="Headline template"
            hint="Use {days} to insert the days-left number (also {hours} available)."
          >
            <Input
              value={s.countdownHeadline || ""}
              onChange={(e) => patch({ countdownHeadline: e.target.value })}
              placeholder="Only {days} days left — register now."
              data-testid="admin-extras-countdown-headline"
            />
          </Field>
        </div>
      </Card>

      {/* 2 · News ticker ───────────────────────────────────────────── */}
      <Card
        testid="admin-extras-news"
        icon={Bell}
        eyebrow="News ticker"
        title="Sliding announcement strip"
        description="Runs along the very top of the Home page. Each item can link to any internal route."
        enabled={s.newsEnabled}
        onToggle={(v) => patch({ newsEnabled: v })}
      >
        <div className="space-y-3">
          {newsItems.length === 0 && (
            <p className="text-sm text-gray-500">No news items yet — add one below.</p>
          )}
          {newsItems.map((n, idx) => (
            <div
              key={n.id || idx}
              className="grid gap-3 rounded-2xl border border-gray-200 bg-gray-50/50 p-3 md:grid-cols-[1fr_180px_auto_auto]"
              data-testid={`admin-extras-news-item-${idx}`}
            >
              <Input
                value={n.text || ""}
                onChange={(e) => updNews(idx, { text: e.target.value })}
                placeholder="Announcement text (e.g. Early-bird closes 30 Nov)"
              />
              <Input
                value={n.url || ""}
                onChange={(e) => updNews(idx, { url: e.target.value })}
                placeholder="/register or https://…"
              />
              <label className="flex items-center gap-2 text-xs font-semibold text-apicon-navy">
                <Switch
                  checked={!!n.active}
                  onCheckedChange={(v) => updNews(idx, { active: v })}
                />
                {n.active ? "Active" : "Off"}
              </label>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => delNews(idx)}
                className="text-red-600 hover:bg-red-50"
                data-testid={`admin-extras-news-remove-${idx}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            onClick={addNews}
            className="rounded-full"
            data-testid="admin-extras-news-add"
          >
            <Plus className="mr-2 h-4 w-4" /> Add news item
          </Button>
        </div>
      </Card>

      {/* 3 · Abstract callout ─────────────────────────────────────── */}
      <Card
        testid="admin-extras-abstract"
        icon={FileText}
        eyebrow="Abstract callout"
        title="Below-hero brochure block"
        description="Highlights the abstract-submission call with a downloadable PDF."
        enabled={s.abstractEnabled}
        onToggle={(v) => patch({ abstractEnabled: v })}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Eyebrow">
            <Input
              value={s.abstractEyebrow || ""}
              onChange={(e) => patch({ abstractEyebrow: e.target.value })}
            />
          </Field>
          <Field label="Title">
            <Input
              value={s.abstractTitle || ""}
              onChange={(e) => patch({ abstractTitle: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Teaser (40–60 words)">
          <Textarea
            value={s.abstractTeaser || ""}
            rows={3}
            onChange={(e) => patch({ abstractTeaser: e.target.value })}
          />
        </Field>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-[0.16em] text-apicon-navy/80">
            Highlight chips
          </Label>
          <div className="mt-2 space-y-2">
            {chips.map((c, idx) => (
              <div key={c.id || idx} className="flex items-center gap-2">
                <Input
                  value={c.text || ""}
                  onChange={(e) => updChip(idx, e.target.value)}
                  placeholder="e.g. Deadline · 30 Nov 2026"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => delChip(idx)}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" onClick={addChip} className="rounded-full">
              <Plus className="mr-2 h-4 w-4" /> Add chip
            </Button>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-[1fr_240px]">
          <Field
            label="PDF URL"
            hint="Use a permanent link. Object-storage or Emergent-artifact URLs work."
          >
            <Input
              value={s.abstractPdfUrl || ""}
              onChange={(e) => patch({ abstractPdfUrl: e.target.value })}
              placeholder="https://…/abstract-brochure.pdf"
            />
          </Field>
          <Field label="Download filename">
            <Input
              value={s.abstractPdfFilename || ""}
              onChange={(e) => patch({ abstractPdfFilename: e.target.value })}
              placeholder="APICON-2026-Abstract.pdf"
            />
          </Field>
        </div>
        {s.abstractPdfUrl && (
          <a
            href={s.abstractPdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-apicon-navy hover:text-apicon-green"
          >
            <ExternalLink className="h-3 w-3" /> Preview PDF
          </a>
        )}
      </Card>

      {/* 4 · Explore Tinsukia ─────────────────────────────────────── */}
      <Card
        testid="admin-extras-tourism"
        icon={MapPin}
        eyebrow="Explore Tinsukia"
        title="Tourism videos & photos"
        description="Curated media from Tinsukia and nearby — YouTube embeds + photo tiles."
        enabled={s.tourismEnabled}
        onToggle={(v) => patch({ tourismEnabled: v })}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Eyebrow">
            <Input
              value={s.tourismEyebrow || ""}
              onChange={(e) => patch({ tourismEyebrow: e.target.value })}
            />
          </Field>
          <Field label="Title">
            <Input
              value={s.tourismTitle || ""}
              onChange={(e) => patch({ tourismTitle: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Body / description">
          <Textarea
            value={s.tourismBody || ""}
            rows={3}
            onChange={(e) => patch({ tourismBody: e.target.value })}
          />
        </Field>

        <div>
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-apicon-navy/80">
            <Youtube className="h-4 w-4 text-apicon-gold" /> Videos
          </div>
          <div className="space-y-2">
            {tvids.map((v, idx) => (
              <div
                key={v.id || idx}
                className="grid gap-2 rounded-2xl border border-gray-200 bg-gray-50/50 p-3 md:grid-cols-[220px_1fr_auto]"
                data-testid={`admin-extras-tourism-video-${idx}`}
              >
                <Input
                  value={v.youtubeId || ""}
                  onChange={(e) => updTvid(idx, { youtubeId: e.target.value })}
                  placeholder="YouTube URL or ID (paste from YouTube)"
                />
                <Input
                  value={v.caption || ""}
                  onChange={(e) => updTvid(idx, { caption: e.target.value })}
                  placeholder="Caption"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => delTvid(idx)}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" onClick={addTvid} className="rounded-full">
              <Plus className="mr-2 h-4 w-4" /> Add YouTube video
            </Button>
          </div>
        </div>

        <div>
          <div className="mb-2 mt-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-apicon-navy/80">
            <ImageIcon className="h-4 w-4 text-apicon-gold" /> Photos
          </div>
          <div className="space-y-2">
            {tphotos.map((p, idx) => (
              <div
                key={p.id || idx}
                className="grid gap-2 rounded-2xl border border-gray-200 bg-gray-50/50 p-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]"
                data-testid={`admin-extras-tourism-photo-${idx}`}
              >
                <Input
                  value={p.url || ""}
                  onChange={(e) => updTphoto(idx, { url: e.target.value })}
                  placeholder="https://…/photo.jpg"
                />
                <Input
                  value={p.caption || ""}
                  onChange={(e) => updTphoto(idx, { caption: e.target.value })}
                  placeholder="Caption"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => delTphoto(idx)}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" onClick={addTphoto} className="rounded-full">
              <Plus className="mr-2 h-4 w-4" /> Add photo URL
            </Button>
          </div>
        </div>
      </Card>

      {/* Sticky save bar */}
      <div className="sticky bottom-4 flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="rounded-full bg-apicon-navy px-8 font-semibold text-white shadow-xl hover:bg-[#082C5A]"
          data-testid="admin-extras-save"
        >
          <Save className="mr-2 h-4 w-4" />
          {saving ? "Saving…" : "Save all extras"}
        </Button>
      </div>
    </div>
  );
};

export default AdminExtras;
